'use client'

import { cn } from '@/lib/utils'
import type { DashboardPayload, SVSPayload } from '../svs-types'

interface DashboardRendererProps {
  payload: DashboardPayload
  /** Recursive render function passed in from SVSRenderer */
  renderChild: (child: SVSPayload, index: number) => React.ReactNode
}

/** Types that should always span full width in grid layouts */
const FULL_WIDTH_TYPES = new Set(['metric-row', 'table'])

function getLayoutClasses(layout: string | undefined, componentCount: number): string {
  switch (layout) {
    case 'grid-2':
      return 'grid grid-cols-1 gap-4 md:grid-cols-2'
    case 'grid-3':
      return 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'
    case 'grid-2x2':
      return 'grid grid-cols-1 gap-4 md:grid-cols-2'
    case 'stack':
      return 'flex flex-col gap-4'
    case 'sidebar-left':
      return 'grid grid-cols-1 gap-4 md:grid-cols-3'
    case 'sidebar-right':
      return 'grid grid-cols-1 gap-4 md:grid-cols-3'
    case 'auto':
    default:
      // Auto: use grid-2 for 2-4 items, grid-3 for 5+, stack for 1
      if (componentCount <= 1) return 'flex flex-col gap-4'
      if (componentCount <= 4) return 'grid grid-cols-1 gap-4 md:grid-cols-2'
      return 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'
  }
}

function getItemClasses(
  layout: string | undefined,
  index: number,
  total: number,
  itemType: string,
): string {
  // Full-width types always span full grid
  if (FULL_WIDTH_TYPES.has(itemType)) return 'col-span-full'

  if (layout === 'sidebar-left') {
    return index === 0 ? 'md:col-span-1' : 'md:col-span-2'
  }
  if (layout === 'sidebar-right') {
    return index === total - 1 ? 'md:col-span-1' : 'md:col-span-2'
  }

  return ''
}

export function DashboardRenderer({ payload, renderChild }: DashboardRendererProps) {
  const { layout, components } = payload.data
  const layoutClass = getLayoutClasses(layout, components.length)

  return (
    <div className={cn(layoutClass)}>
      {components.map((child, i) => (
        <div
          key={i}
          className={cn(
            'rounded-lg border border-border bg-card p-4',
            getItemClasses(layout, i, components.length, child.type),
          )}
        >
          {child.title && (
            <h4 className="mb-3 text-sm font-semibold">{child.title}</h4>
          )}
          {renderChild(child, i)}
        </div>
      ))}
    </div>
  )
}
